import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import clinicApi, { MEDIA_BASE_URL } from "../../api/clinicClient";
import PortalLayout from "../layout/PortalLayout";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function doctorLabel(appt) {
  return `Dr. ${appt.doctor_name}${appt.doctor_specialty ? ` (${appt.doctor_specialty})` : ""}`;
}

const PAYMENT_CFG = {
  unpaid:         { bg: "#fef2f2", color: "#dc2626", label: "Unpaid" },
  partially_paid: { bg: "#fef3c7", color: "#b45309", label: "Partially Paid" },
  paid:           { bg: "#ecfdf5", color: "#059669", label: "Paid" },
  waived:         { bg: "#f3f4f6", color: "#374151", label: "Waived" },
  cancelled:      { bg: "#f3f4f6", color: "#6b7280", label: "Cancelled" },
};

function PaymentStatusTag({ status }) {
  const s = PAYMENT_CFG[status] ?? { bg: "#f3f4f6", color: "#6b7280", label: status };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      background: s.bg, color: s.color,
      borderRadius: 20, padding: "3px 10px", fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {s.label}
    </span>
  );
}

const STATUS_CFG = {
  booked:      { bg: "#eff6ff", color: "#2563eb", label: "Booked" },
  checked_in:  { bg: "#fef3c7", color: "#b45309", label: "Checked In" },
  in_progress: { bg: "#fef3c7", color: "#b45309", label: "In Progress" },
  seen:        { bg: "#f3f4f6", color: "#374151", label: "Seen" },
  completed:   { bg: "#ecfdf5", color: "#059669", label: "Completed" },
  no_show:     { bg: "#fef2f2", color: "#dc2626", label: "No Show" },
  cancelled:   { bg: "#fef2f2", color: "#dc2626", label: "Cancelled" },
};

function StatusTag({ status }) {
  const s = STATUS_CFG[status] ?? { bg: "#f3f4f6", color: "#6b7280", label: status };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      background: s.bg, color: s.color,
      borderRadius: 20, padding: "3px 10px", fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {s.label}
    </span>
  );
}

// ── Card — view-only, opens the detail dialog. No cancel action here. ──────

function AppointmentCard({ appt, onView }) {
  return (
    <div className="ph-card" style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10, cursor: "pointer" }} onClick={() => onView(appt)}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>
          <i className="pi pi-building" style={{ fontSize: 10, marginRight: 5 }} />
          {appt.clinic_name}
        </span>
        <StatusTag status={appt.status} />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10, flexShrink: 0,
          background: "var(--brand-light)", color: "var(--brand-primary)",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17,
          overflow: "hidden",
        }}>
          {appt.doctor_photo_url ? (
            <img src={`${MEDIA_BASE_URL}${appt.doctor_photo_url}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <i className="pi pi-user" />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>{doctorLabel(appt)}</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            {formatDate(appt.scheduled_at)}
            {appt.booking_mode === "token"
              ? ` · Token #${appt.token_number} · Session ${appt.session_start_time}`
              : ` · ${new Date(appt.scheduled_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`}
            {appt.is_self === false && ` · For ${appt.patient_name}`}
          </div>
        </div>
        <i className="pi pi-chevron-right" style={{ color: "var(--text-muted)", fontSize: 13 }} />
      </div>
    </div>
  );
}

// ── Detail dialog — the only place cancellation lives, behind a second confirm step ──

function AppointmentDetailDialog({ appt, onClose, onCancelled }) {
  const [moreActionsOpen, setMoreActionsOpen] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const toast = useRef(null);

  if (!appt) return null;
  // Only reachable while still BOOKED — once checked in the patient is
  // already at the clinic, so it's no longer a self-service cancellation
  // (see clinic.views_patient.cancel_appointment_patient).
  const cancellable = appt.status === "booked";

  const doCancel = () => {
    setCancelling(true);
    clinicApi.post(`appointments/${appt.id}/cancel/`)
      .then(() => {
        onCancelled();
        onClose();
      })
      .catch(err => {
        const data = err.response?.data || {};
        const detail = data.clinic_contact_phone
          ? `${data.error} Clinic contact: ${data.clinic_contact_phone}`
          : (data.error || "Please try again.");
        toast.current?.show({ severity: "error", summary: "Could not cancel", detail, life: 6000 });
        setConfirmingCancel(false);
      })
      .finally(() => setCancelling(false));
  };

  return (
    <Dialog header="Appointment Details" visible onHide={onClose} style={{ width: "min(460px, 95vw)" }} draggable={false} resizable={false}>
      <Toast ref={toast} />
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, padding: "12px 14px", background: "var(--surface-bg)", borderRadius: 10, fontSize: 13 }}>
          <div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Status</div>
            <StatusTag status={appt.status} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Doctor</div>
            <div style={{ fontWeight: 600 }}>{doctorLabel(appt)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Clinic</div>
            <div style={{ fontWeight: 600 }}>{appt.clinic_name}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Patient</div>
            <div style={{ fontWeight: 600 }}>{appt.patient_name}{appt.is_self && " (you)"}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Date</div>
            <div style={{ fontWeight: 600 }}>{formatDate(appt.scheduled_at)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>
              {appt.booking_mode === "token" ? "Session" : "Time"}
            </div>
            <div style={{ fontWeight: 600 }}>
              {appt.booking_mode === "token"
                ? `Token #${appt.token_number} · ${appt.session_start_time}`
                : new Date(appt.scheduled_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
          {appt.estimated_time && (
            <div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 2 }}>Estimated Time</div>
              <div style={{ fontWeight: 600 }}>
                {appt.estimated_time}{appt.delay_minutes > 0 ? ` (running ${appt.delay_minutes}m late)` : ""}
              </div>
            </div>
          )}
        </div>

        {appt.notes && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 4 }}>Notes</div>
            <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>{appt.notes}</div>
          </div>
        )}

        {appt.invoice_summary && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 8 }}>
              Payment Details
            </div>
            <div style={{ background: "var(--surface-bg)", borderRadius: 10, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
              {appt.invoice_summary.items.map((item, idx) => (
                <div key={idx} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: "var(--text-secondary)" }}>{item.description}</span>
                  <span style={{ fontWeight: 600 }}>Rs. {parseFloat(item.amount).toLocaleString("en-LK", { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700, paddingTop: 8, borderTop: "1px solid var(--border-color)" }}>
                <span>Total</span>
                <span>Rs. {parseFloat(appt.invoice_summary.total_amount).toLocaleString("en-LK", { minimumFractionDigits: 2 })}</span>
              </div>
              {parseFloat(appt.invoice_summary.paid_amount) > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#059669" }}>
                  <span>Paid</span>
                  <span style={{ fontWeight: 600 }}>Rs. {parseFloat(appt.invoice_summary.paid_amount).toLocaleString("en-LK", { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              {parseFloat(appt.invoice_summary.balance_due) > 0.01 && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 600, color: "#b45309" }}>
                  <span>Balance Due</span>
                  <span>Rs. {parseFloat(appt.invoice_summary.balance_due).toLocaleString("en-LK", { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>Payment Status</span>
                <PaymentStatusTag status={appt.invoice_summary.payment_status} />
              </div>
            </div>
          </div>
        )}

        {cancellable && (
          <div>
            <button
              onClick={() => setMoreActionsOpen(o => !o)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "none", border: "none", cursor: "pointer", fontFamily: "inherit",
                fontSize: 12.5, fontWeight: 600, color: "var(--text-muted)", padding: "4px 0",
              }}
            >
              <i className={`pi ${moreActionsOpen ? "pi-chevron-down" : "pi-chevron-right"}`} style={{ fontSize: 10 }} />
              More actions
            </button>

            {moreActionsOpen && !confirmingCancel && (
              <button
                onClick={() => setConfirmingCancel(true)}
                style={{
                  marginTop: 8, padding: "10px", borderRadius: 10, fontSize: 13, fontWeight: 600, fontFamily: "inherit",
                  background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", cursor: "pointer", width: "100%",
                }}
              >
                Cancel Appointment
              </button>
            )}
          </div>
        )}

        {cancellable && moreActionsOpen && confirmingCancel && (
          <div style={{ padding: "12px 14px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 13, color: "#991b1b", fontWeight: 600 }}>
              Are you sure? This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => setConfirmingCancel(false)}
                style={{
                  flex: 1, padding: "9px", borderRadius: 8, fontSize: 12.5, fontWeight: 600, fontFamily: "inherit",
                  background: "white", color: "var(--text-secondary)", border: "1px solid var(--border-color)", cursor: "pointer",
                }}
              >
                Keep Appointment
              </button>
              <button
                onClick={doCancel}
                disabled={cancelling}
                style={{
                  flex: 1, padding: "9px", borderRadius: 8, fontSize: 12.5, fontWeight: 700, fontFamily: "inherit",
                  background: "#dc2626", color: "white", border: "none", cursor: cancelling ? "not-allowed" : "pointer", opacity: cancelling ? 0.6 : 1,
                }}
              >
                {cancelling ? "Cancelling…" : "Yes, Cancel It"}
              </button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

export default function MyAppointments() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("upcoming");
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    clinicApi.get("appointments/")
      .then(res => setAppointments(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const now = new Date();
  const filtered = appointments.filter(a => {
    if (filter === "all") return true;
    const upcoming = new Date(a.scheduled_at) >= now && !["completed", "cancelled", "no_show"].includes(a.status);
    return filter === "upcoming" ? upcoming : !upcoming;
  });

  return (
    <PortalLayout>
      <AppointmentDetailDialog appt={selected} onClose={() => setSelected(null)} onCancelled={load} />

      <div className="page-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h1 className="page-title">My Appointments</h1>
          <p className="page-subtitle">Across every clinic you've visited</p>
        </div>
        <button
          onClick={() => navigate("/find-doctor")}
          style={{
            padding: "9px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600, fontFamily: "inherit",
            background: "var(--brand-primary)", color: "white", border: "none", cursor: "pointer",
          }}
        >
          <i className="pi pi-plus" style={{ fontSize: 11, marginRight: 6 }} />
          Book New
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[{ key: "upcoming", label: "Upcoming" }, { key: "past", label: "Past" }, { key: "all", label: "All" }].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            style={{
              padding: "6px 14px", borderRadius: 20, fontSize: 12.5, fontWeight: 500,
              border: `1.5px solid ${filter === key ? "var(--brand-primary)" : "var(--border-color)"}`,
              background: filter === key ? "var(--brand-primary)" : "transparent",
              color: filter === key ? "white" : "var(--text-secondary)",
              cursor: "pointer", fontFamily: "inherit",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-muted)" }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: 28, display: "block", marginBottom: 12, color: "var(--brand-primary)" }} />
          <div style={{ fontSize: 13 }}>Loading appointments…</div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-muted)" }}>
          <i className="pi pi-calendar" style={{ fontSize: 32, display: "block", marginBottom: 12 }} />
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>No appointments</div>
          <div style={{ fontSize: 13 }}>Book your first appointment with a doctor.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map(a => <AppointmentCard key={a.id} appt={a} onView={setSelected} />)}
        </div>
      )}
    </PortalLayout>
  );
}
